import * as React from "react";
import type { ModelChoice } from "@lupp/shared";

export interface ModelSelectorProps {
  selectedId: string;
  models: ModelChoice[];
  onSelect: (model: ModelChoice) => void;
}

interface ModelSelectorState {
  open: boolean;
  query: string;
}

export default class ModelSelector extends React.Component<ModelSelectorProps, ModelSelectorState> {
  private rootRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.state = { open: false, query: "" };
  }

  componentDidMount(): void {
    document.addEventListener("mousedown", this.handleOutside);
  }

  componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.handleOutside);
  }

  handleOutside = (e: MouseEvent): void => {
    if (this.rootRef.current && !this.rootRef.current.contains(e.target as Node)) {
      this.setState({ open: false });
    }
  };

  toggle = (): void => this.setState((s) => ({ open: !s.open, query: "" }));

  select = (model: ModelChoice): void => {
    this.props.onSelect(model);
    this.setState({ open: false });
  };

  render(): JSX.Element {
    const { selectedId, models } = this.props;
    const { open, query } = this.state;

    const selected = models.find((m) => m.id === selectedId) ?? models[0];

    const filtered = models.filter((m) => m.name.toLowerCase().includes(query.toLowerCase()));

    return (
      <div className="model-sel" ref={this.rootRef}>
        {/* trigger button */}
        <button
          className="model-sel__trigger"
          type="button"
          onClick={this.toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="model-sel__name">{selected?.name ?? "Modell wählen"}</span>
          <svg
            className={`model-sel__chevron${open ? " model-sel__chevron--open" : ""}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div className="model-sel__dropdown" role="listbox">
            {/* search */}
            <div className="model-sel__search-row">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="model-sel__search-icon"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                className="model-sel__search"
                type="text"
                placeholder="Ein Modell suchen"
                value={query}
                onChange={(e): void => this.setState({ query: e.target.value })}
                autoFocus
                aria-label="Modell suchen"
              />
            </div>

            {/* model list */}
            <div className="model-sel__list">
              {filtered.map((m) => {
                const isSelected = m.id === selectedId;
                return (
                  <button
                    key={m.id}
                    className={`model-sel__item${isSelected ? " model-sel__item--active" : ""}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={(): void => this.select(m)}
                  >
                    {/* icon — model image masked to a circle, or a fallback */}
                    <span className="model-sel__item-icon">
                      {m.image ? (
                        <img className="model-sel__item-img" src={m.image} alt="" />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M10 6v8M6 10h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>

                    {/* name */}
                    <span className="model-sel__item-name">{m.name}</span>

                    {/* description → info icon with hover tooltip (only when present) */}
                    {m.description && m.description.trim() && (
                      <span className="model-sel__info" title={m.description} aria-label={m.description}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                          />
                        </svg>
                      </span>
                    )}

                    {/* checkmark for selected */}
                    {isSelected && (
                      <svg
                        className="model-sel__check"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
}
